import { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { Typography } from '@mui/material';
import { Box } from '@mui/system';
import TagsTable from '../../../../components/Table/Table';
import { graphql, DocumentType } from '../../../../gql';
import { useApiQuery } from '../../../../query/hooks';
import { z } from 'zod';

import { useEditModal } from '../../../../modal/useEditModal';
import { TagsEdit } from '../../../../journey/detail/detailView/widgets/tags/TagsEdit';
import { TagsTableRow } from './TagsTableRow';
import Paginator from '../../../../components/Table/Paginator';
import { PAGE_SIZE } from '../../../../config';

const paginateTagQuery = graphql(`
	query paginateTagQuery($first: Int, $skip: Int) {
		paginateTag(first: $first, skip: $skip) {
			edges {
				node {
					id
					name
					paginateActivities(filter: { touchpoint: { phase: { journey: { isTemplate: { eq: false } } } } }) {
						pageInfo {
							totalCount
						}
					}
				}
			}
			pageInfo {
				totalCount
			}
		}
	}
`);
const tags_listTag = graphql(`
	query tags_listTag {
		listTag {
			id
		}
	}
`);

export type Tag = DocumentType<typeof paginateTagQuery>['paginateTag']['edges'][number]['node'];

export const editTagSchema = z.object({
	tagId: z.string(),
});

const TagsResourcePage: FC = () => {
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(PAGE_SIZE);
	const { data, isLoading } = useApiQuery(paginateTagQuery, {
		variables: {
			skip: (page - 1) * pageSize,
			first: pageSize,
		},
	});

	const { data: allTags, isLoading: allTgasLoading } = useApiQuery(tags_listTag);
	const { state, openCreate, openUpdate, close } = useEditModal<Tag>();

	const tagList = data?.paginateTag.edges.map((item) => item.node) ?? [];

	const tableHeaderData = [
		{ text: <FormattedMessage id={'resources.tags.table.column.name'} /> },
		{ text: <FormattedMessage id={'resources.tags.table.column.uses'} /> },
		{ text: '' },
	];
	const tableRows = tagList.map((row) => <TagsTableRow row={row} onEdit={openUpdate} />);

	return (
		<>
			<Typography variant={'body1'} fontWeight={700}>
				<FormattedMessage id={'resources.tags.title'} />
			</Typography>

			<Box mt={3}>
				<TagsTable
					variant={'withTitle'}
					title={<FormattedMessage id={'resources.tags.table.title'} />}
					action={openCreate}
					actionTitle={<FormattedMessage id={'resources.tags.table.button.create'} />}
					totalCount={allTags?.listTag.length ?? 0}
					isLoading={isLoading || allTgasLoading}
					headerData={tableHeaderData}
					tableRows={tableRows}
				/>
				<Paginator
					totalCount={data?.paginateTag.pageInfo.totalCount || 0}
					pageSize={pageSize}
					page={page}
					setPage={setPage}
					setPageSize={setPageSize}
				/>
				<TagsEdit state={state} onSave={close} onCancel={close} />
			</Box>
		</>
	);
};

export default TagsResourcePage;
